import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { CheckCircle, XCircle, ArrowLeft, Scan, Dumbbell } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { daysUntilExpiry, getTodayString } from '../utils/helpers';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const SCAN_INTERVAL = 150; // ms

const Kiosk = () => {
  const navigate = useNavigate();
  const { members, attendances, addAttendance, memberships, gymSettings } = useApp();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanRef = useRef(0);

  const [result, setResult] = useState(null); // { status: 'success'|'error'|'warning', member, message }
  const [cameraError, setCameraError] = useState('');
  const [scanning, setScanning] = useState(true);

  const today = getTodayString();
  const [clockTime, setClockTime] = useState('');
  const [clockDate, setClockDate] = useState('');

  useEffect(() => {
    function tick() {
      const n = new Date();
      setClockTime(format(n, 'HH:mm:ss', { locale: ko }));
      setClockDate(format(n, 'yyyy년 M월 d일 (EEE)', { locale: ko }));
    }
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // 카메라 시작
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setCameraError('카메라를 사용할 수 없습니다. 브라우저 권한을 확인해 주세요.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
  }, []);

  // QR 스캔 루프
  const scanLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !scanning) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const now = Date.now();
      if (now - lastScanRef.current > SCAN_INTERVAL) {
        lastScanRef.current = now;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });

        if (code?.data?.startsWith('gym-member:')) {
          const memberId = code.data.replace('gym-member:', '');
          handleCheckin(memberId);
          return; // 스캔 루프 일시 중단
        }
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [scanning]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  useEffect(() => {
    if (scanning && !cameraError) {
      rafRef.current = requestAnimationFrame(scanLoop);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [scanning, scanLoop, cameraError]);

  const handleCheckin = useCallback((memberId) => {
    setScanning(false);
    const member = members.find(m => m.id === memberId);

    if (!member) {
      setResult({ status: 'error', message: '등록되지 않은 회원입니다.', member: null });
      setTimeout(() => { setResult(null); setScanning(true); }, 3000);
      return;
    }

    const alreadyChecked = attendances.some(a => a.memberId === memberId && a.date === today);
    if (alreadyChecked) {
      setResult({ status: 'warning', member, message: '이미 오늘 출석하셨습니다.' });
      setTimeout(() => { setResult(null); setScanning(true); }, 3000);
      return;
    }

    // 회원권 확인
    const activeMemberships = memberships.filter(ms => ms.memberId === memberId && !ms.frozenAt);
    const soonestExpiry = activeMemberships.reduce((min, ms) => {
      const d = daysUntilExpiry(ms.endDate);
      return (d !== null && d >= 0 && (min === null || d < min)) ? d : min;
    }, null);

    addAttendance(memberId);

    let message = '출석이 완료되었습니다.';
    let status = 'success';
    if (soonestExpiry !== null && soonestExpiry <= 7) {
      message = `출석 완료! 회원권이 ${soonestExpiry === 0 ? '오늘' : `${soonestExpiry}일 후`} 만료됩니다.`;
      status = 'warning';
    } else if (activeMemberships.length === 0) {
      message = '출석 완료! 유효한 회원권이 없습니다. 재등록을 안내해 주세요.';
      status = 'warning';
    }

    setResult({ status, member, message });
    setTimeout(() => { setResult(null); setScanning(true); }, 3500);
  }, [members, attendances, memberships, addAttendance, today]);

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col z-50">
      {/* 상단 헤더 */}
      <div className="flex items-center justify-between px-6 py-4 bg-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-white text-sm">{gymSettings?.name || 'GYM Manager'}</p>
            <p className="text-xs text-gray-400">키오스크 출석 모드</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-white font-bold text-lg tabular-nums">{clockTime}</p>
          <p className="text-gray-400 text-xs">{clockDate}</p>
        </div>
        <button onClick={() => { stopCamera(); navigate('/attendance'); }} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> 나가기
        </button>
      </div>

      {/* 카메라 뷰 */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center bg-black">
        {cameraError ? (
          <div className="text-center text-gray-400 p-8">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <p className="text-lg font-medium text-white mb-2">카메라 오류</p>
            <p className="text-sm">{cameraError}</p>
          </div>
        ) : (
          <>
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            <canvas ref={canvasRef} className="hidden" />

            {/* QR 스캔 가이드 박스 */}
            {scanning && !result && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                {/* 어두운 배경 마스크 */}
                <div className="absolute inset-0 bg-black/40" />
                {/* 가이드 사각형 */}
                <div className="relative w-56 h-56 sm:w-72 sm:h-72">
                  {/* 코너 마커 */}
                  {[['top-0 left-0', 'border-t-4 border-l-4'],
                    ['top-0 right-0', 'border-t-4 border-r-4'],
                    ['bottom-0 left-0', 'border-b-4 border-l-4'],
                    ['bottom-0 right-0', 'border-b-4 border-r-4']].map(([pos, border], i) => (
                    <div key={i} className={`absolute w-8 h-8 ${pos} ${border} border-primary-400 rounded-sm`} />
                  ))}
                  {/* 스캔 라인 애니메이션 */}
                  <div className="absolute inset-x-0 h-0.5 bg-primary-400 opacity-80 animate-pulse" style={{ top: '50%' }} />
                </div>
                <div className="relative mt-6 flex items-center gap-2 bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                  <Scan className="w-4 h-4 text-primary-400" />
                  회원 QR코드를 스캔해 주세요
                </div>
              </div>
            )}

            {/* 결과 오버레이 */}
            {result && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
                <div className={`flex flex-col items-center p-8 rounded-3xl text-center max-w-sm mx-4 ${
                  result.status === 'success' ? 'bg-green-900/80 border-2 border-green-500' :
                  result.status === 'warning' ? 'bg-yellow-900/80 border-2 border-yellow-500' :
                  'bg-red-900/80 border-2 border-red-500'
                }`}>
                  {result.status === 'success' ? (
                    <CheckCircle className="w-20 h-20 text-green-400 mb-4" />
                  ) : result.status === 'warning' ? (
                    <CheckCircle className="w-20 h-20 text-yellow-400 mb-4" />
                  ) : (
                    <XCircle className="w-20 h-20 text-red-400 mb-4" />
                  )}
                  {result.member && (
                    <>
                      {result.member.photo ? (
                        <img src={result.member.photo} alt="" className="w-20 h-20 rounded-full object-cover mb-3 border-4 border-white/20" />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-primary-600 flex items-center justify-center mb-3">
                          <span className="text-3xl font-bold text-white">{result.member.name[0]}</span>
                        </div>
                      )}
                      <p className="text-3xl font-bold text-white mb-1">{result.member.name}</p>
                      <p className="text-gray-300 text-sm mb-3">{result.member.phone}</p>
                    </>
                  )}
                  <p className={`text-lg font-medium ${
                    result.status === 'success' ? 'text-green-300' :
                    result.status === 'warning' ? 'text-yellow-300' : 'text-red-300'
                  }`}>{result.message}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 하단 안내 */}
      <div className="bg-gray-800 px-6 py-3 flex items-center justify-center gap-2 text-gray-400 text-sm">
        <Scan className="w-4 h-4" />
        회원 앱 또는 회원 상세 페이지에서 QR코드를 발급받으세요
      </div>
    </div>
  );
};

export default Kiosk;
